import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, ChevronDownIcon, LanguagesIcon, PhoneIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { business } from '../data/business';
import { faqs } from '../data/faqs';
import { ui } from '../data/ui';
import type { FaqGroup } from '../types/content';
const groupOrder: FaqGroup[] = ['general', 'commercial', 'residential', 'more'];
export function FaqSection() {
  const {
    t,
    lang,
    setLang
  } = useLanguage();
  const [open, setOpen] = useState<number | null>(0);
  const other = lang === 'en' ? 'es' : 'en';
  const indexed = faqs.map((f, i) => ({
    ...f,
    i
  }));
  return <section id="faq" className="bg-white py-20 md:py-24" aria-labelledby="faq-title" data-id="element-83" data-mp="1zhfx3jebknnn">
      <div className="container-page grid gap-12 lg:grid-cols-12 lg:gap-16" data-id="element-84" data-mp="2n7ipy1q25xnb">
        <div className="lg:col-span-4" data-id="element-85" data-mp="2rysd8vo4wow1">
          <div className="lg:sticky lg:top-36" data-id="element-86" data-mp="2rzk6w0y68cjo">
            <h2 id="faq-title" className="font-display text-[28px] font-bold leading-tight text-ink md:text-4xl" data-id="element-87" data-mp="14va9hr568kz7">
              {t(ui.faq.title)}
              <span lang={other} className="mt-1 block text-xl font-semibold text-muted md:text-2xl" data-id="element-88" data-mp="10uqg8ynpy1ic">
                {ui.faq.title[other]}
              </span>
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-body" data-id="element-89" data-mp="19tva3roc56o6">{t(ui.faq.subtitle)}</p>
            <button type="button" onClick={() => setLang(other)} lang={other} className="mt-6 inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-line px-4 text-sm font-semibold text-navy transition-colors duration-150 hover:border-navy" data-id="element-90" data-mp="1oj18xej9guvj">
              <LanguagesIcon className="h-4 w-4" aria-hidden="true" data-id="element-91" data-mp="1slxxdlvhfl7e" />
              {t(ui.faq.switchLang)}
            </button>

            <div className="mt-10 border-t border-line pt-8" data-id="element-92" data-mp="366v9g1b26v1">
              <p className="font-display text-lg font-bold text-ink" data-id="element-93" data-mp="3ud19kfl0t7u2">{t(ui.faq.stillQuestions)}</p>
              <p className="mt-1 text-body" data-id="element-94" data-mp="1y9wjb39ncxlm">{t(ui.faq.stillBody)}</p>
              <div className="mt-4 flex flex-col gap-2 text-sm font-semibold" data-id="element-95" data-mp="2386f9g37428a">
                <a href={business.phoneHref} className="inline-flex items-center gap-2 text-navy hover:underline" data-id="element-96" data-mp="23aahzupcyv6z">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-97" data-mp="2c8w768pc58gq" />
                  {business.phoneDisplay}
                </a>
                <a href="#contact" className="inline-flex items-center gap-2 text-leaf-strong hover:underline" data-id="element-98" data-mp="2h8qvq5ln1lym">
                  {t(ui.faq.contactLink)}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-99" data-mp="2m4pendo76brn" />
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-12 lg:col-span-8" data-id="element-100" data-mp="2vvk38oox1ng9">
          {groupOrder.map(group => <div key={group} data-id="element-101" data-mp="2zzi1kx6f2u89">
              <h3 className="font-display text-sm font-bold uppercase tracking-wider text-leaf-strong" data-id="element-102" data-mp="3009v82ggehvw">{t(ui.faq.groups[group])}</h3>
              <ul className="mt-3 divide-y divide-line border-y border-line" data-id="element-103" data-mp="t9ya1lvr46pt">
                {indexed.filter(f => f.group === group).map(f => {
              const isOpen = open === f.i;
              return <li key={f.question.en} data-id="element-104" data-mp="kbmbmxon84mt">
                        <h4 data-id="element-105" data-mp="fb6z28c6r15w">
                          <button type="button" onClick={() => setOpen(isOpen ? null : f.i)} aria-expanded={isOpen} aria-controls={`faq-panel-${f.i}`} id={`faq-button-${f.i}`} className="flex min-h-[64px] w-full items-center justify-between gap-4 py-4 text-left font-display text-base font-bold text-ink transition-colors duration-150 hover:text-navy md:text-lg" data-id="element-106" data-mp="fbzwujt62gbw">
                            {t(f.question)}
                            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors duration-150 ${isOpen ? 'bg-navy text-white' : 'bg-mist text-navy'}`} data-id="element-107" data-mp="1qwnhib983bj2">
                              <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-108" data-mp="11k41n7m09c19" />
                            </span>
                          </button>
                        </h4>
                        <AnimatePresence initial={false} data-id="element-109" data-mp="1y6ywne2sw2gi">
                          {isOpen && <motion.div id={`faq-panel-${f.i}`} role="region" aria-labelledby={`faq-button-${f.i}`} initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.22,
                    ease: [0.23, 1, 0.32, 1]
                  }} className="overflow-hidden" data-id="element-110" data-mp="1kfkef9gpw5ai">
                              <p className="max-w-2xl pb-6 pr-12 leading-relaxed text-body" data-id="element-111" data-mp="310vz2oh7y68m">{t(f.answer)}</p>
                            </motion.div>}
                        </AnimatePresence>
                      </li>;
            })}
              </ul>
            </div>)}
        </div>
      </div>
    </section>;
}